import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import axios from 'axios';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
  Panel
} from 'reactflow';
import 'reactflow/dist/style.css';
import { motion } from 'framer-motion';

import { useToast } from '../components/common/ToastProvider';
import styled from 'styled-components';
import { FaSlidersH, FaTimes } from 'react-icons/fa';
import {
  PageContainer,
  PageHeader,
  PageTitle,
  PageIcon,
  Button,
  fadeIn,
  Modal,
  SubSectionTitle,
  Textarea,
  Divider,
  PipelineButtons
} from '../styles/PageStyles';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChalkboardTeacher, faTrash, faBroom, faTimes, faPlay, faSave, faCog, faChevronCircleLeft, faEye, faEdit,faChevronLeft, faShield } from '@fortawesome/free-solid-svg-icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// Node types
import AgentNode from '../components/pipeline/AgentNode';
import ModelNode from '../components/pipeline/ModelNode';
import KnowledgeNode from '../components/pipeline/KnowledgeNode';
import ToolNode from '../components/pipeline/ToolNode';
import TriggerNode from '../components/pipeline/TriggerNode';
import OutputNode from '../components/pipeline/OutputNode';
import ApiNode from '../components/pipeline/ApiNode';
import ClassifierNode from '../components/pipeline/ClassifierNode';
import CodeNode from '../components/pipeline/CodeNode';
import WebHookNode from '../components/pipeline/WebHookNode';

// Sidebar components
import NodePalette from '../components/pipeline/NodePalette';
import PropertiesPanel from '../components/pipeline/PropertiesPanel';
import { useNavigate, useParams } from 'react-router-dom';
import { createPipeline, getNodesForPipeline, getPipelines, runExecutePipeline, runExecutePipelineV2, resumePipeline, updatePipeline, getPipelineExecutionStatus, getPipelineRuns, getFileNodeConfig } from '../services/pipelineService';
import DataSourceNode from '../components/pipeline/DataSourceNode';
import { uploadFile } from '../services/fileUploadService';
import PipelineExecutionDrawer from '../components/pipeline/PipelineExecutionDrawer';
import EmailOutNode from '../components/pipeline/EmailOutNode';
import FileOperationsNode from '../components/pipeline/FileOperationsNode';
import TaskServiceNode from '../components/pipeline/TaskServiceNode';
import ManualTaskNode from '../components/pipeline/ManualTaskNode';
import { convertNodeNameToLocal, mandatoryFields, nodeDefaults } from '../constants/nodeDefaults';
import TimerNode from '../components/pipeline/TimerNode';
import { usePermission } from '../context/PermissionContext';
import { useAuth } from '../context/AuthContext';
import PlannerNode from '../components/pipeline/PlannerNode';
import EntityOperationNode from '../components/pipeline/EntityOperationNode';
import GuardrailsEditor from '../components/guardrails/GuardrailsEditor';
import DocumentNode from '../components/pipeline/DocumentNode';


const NodePaletteContainer = styled.div`
  height: 100%;
  background: #f6f8fa;
  border-right: 1px solid #eee;
  z-index: 1;
  flex-shrink: 0; /* prevents collapsing */
`;

const FlowContainer = styled.div`
  flex: 1;            
  height: 100%;
  position: relative;
`;

const BackButton = styled.button`
  border: 1px solid #1d4ed8;
  border-radius: 4px;
  padding: 10px;
  background: #fff;
  cursor: pointer;
  color: #000;
  font-size: 14px;

  &:hover {
    color: #111827;
    background: #f0f0f0;
  }
`;

const PipelineNameInput = styled.input`
  border: 1px solid transparent;
  border-radius: 6px;
  padding: 4px 8px;
  font-size: 24px;
  max-height: 700px;
  font-weight: 600;
  color: var(--primary);
  background: transparent;
  outline: none;
  min-width: 540px;
  // font-family: 'Manrope', sans-serif;

  ${({ $editing }) =>
    $editing &&
    `
      background: #ffffff;
      border-color: #2563eb;
      box-shadow: 0 0 0 1px rgba(37, 99, 235, 0.4);
    `}

  &::placeholder {
    color: #9ca3af;
  }
`;

const PipelineNameWrapper = styled.div`
  cursor: text;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const HeaderButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  padding: 0.5rem 1rem;

  background: ${(props) =>
    props.primary ? 'var(--primary)' : 'transparent'};
  color: ${(props) =>
    props.primary ? '#ffffff' : '#374151'};
  border: ${(props) =>
    props.primary ? 'none' : '1px solid #e5e7eb'};

  &:hover {
    background: ${(props) =>
      props.primary ? '#1d4ed8' : '#f9fafb'};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;


const FloatingDrawer = styled.div`
  position: fixed;
  top: 155px; 
  right: 0;
  height: calc(100vh - 144px);
  background: #fff;
  border-left: 1px solid #eee;
  box-shadow: #b7b7b7 -2px 12px 13px;
  min-width: 280px;
  max-width: 700px;
  width: ${props => props.width}px;
  transition: width 0.2s;
  z-index: 1002;
  display: ${props => (props.open ? 'block' : 'none')};
  overflow: auto;
`;

const DrawerToggleButton = styled.button`
  position: fixed;
  top: 160px;
  right: ${props => (props.drawerOpen ? `${props.drawerWidth + 10}px` : '10px')};
  z-index: 1003;
  background: #ffffff;
  color: #666;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0,0,0,0.12);
  cursor: pointer;
`;

const DrawerResizer = styled.div`
  width: 6px;
  cursor: ew-resize;
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  background: #e0e0e0;
  z-index: 1004;
  &:hover {
    background: #bdbdbd;
  }
`;


const nodeTypes = {
  triggerNode: TriggerNode,
  outputNode: OutputNode,
  agentNode: AgentNode,
  modelNode: ModelNode,
  knowledgeNode: KnowledgeNode,
  dataSourceNode: DataSourceNode,
  apiNode: ApiNode,
  taskServiceNode: TaskServiceNode,
  manualTaskNode: ManualTaskNode,
  timerNode: TimerNode,
  classifierNode: ClassifierNode,
  emailOutNode: EmailOutNode,
  codeNode: CodeNode,
  webHookNode: WebHookNode,
  toolNode: ToolNode,
  fileNode: FileOperationsNode,
  plannerNode: PlannerNode,
  entityOperationNode: EntityOperationNode,
  documentNode: DocumentNode,
};

const DesignerContainer = styled.div`
  display: flex;
  height: calc(100vh - 160px);
  background: #f9fafb;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
`;

// Markdown editor container with preview toggle
const MarkdownEditorContainer = styled.div`
  position: relative;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: white;
  height: 200px;
`;

const ToggleButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 10;
  background: ${props => props.active ? 'var(--primary)' : '#f8f9fa'};
  color: ${props => props.active ? 'white' : '#333'};
  border: 1px solid ${props => props.active ? 'var(--primary)' : '#ccc'};
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.active ? 'var(--primary-dark)' : '#e9ecef'};
  }
`;

const MarkdownPreview = styled.div`
  padding: 0.75rem;
  height: 100%;
  overflow-y: auto;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif;
  line-height: 1.6;
  
  h1, h2, h3, h4, h5, h6 { margin: 0.5em 0; }
  p { margin: 0.5em 0; }
  ul, ol { margin: 0.5em 0; padding-left: 1.5em; }
  code { background: #f1f3f4; padding: 2px 4px; border-radius: 3px; }
  pre { background: #f8f9fa; padding: 1rem; border-radius: 4px; overflow-x: auto; }
  blockquote { border-left: 4px solid #dee2e6; padding-left: 1rem; margin: 1rem 0; }
`;

// const FlowContainer = styled.div`
//   flex: 1;
//   height: 100%;
// `;

const DesignerHeader = styled.div`
  height: 56px;
  padding: 0 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const PipelineName = styled.input`
  border: none;
  border-bottom: 2px solid #ccc;
  color: #333 !important; /* Force dark text color */
  position: relative;
  top: -5px;
font-size: 1.8rem;
  font-weight: 700;
  background: var(--gradient-primary);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  // font-family: 'Manrope', sans-serif;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
`;
  const createDefaultTriggerNode = () => ({
    id: `trigger-node-${Date.now()}`,

    type: "triggerNode",

    position: {
      x: 300,
      y: 120
    },

    data: {
      name: "Trigger",
      description: "Starting point of pipeline",
      type: "trigger",
      triggerType: "manual",
      code: `trigger-node-${Date.now()}`
    },
      deletable: false,
  });

const PipelineDesigner = () => {
  const navigate = useNavigate();
  const { pipelineId } = useParams();
  const reactFlowWrapper = useRef(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const selectedNode = useMemo(() => {
    return nodes.find((node) => node.id === selectedNodeId) || null;
  }, [nodes, selectedNodeId]);
  const [pipelineName, setPipelineName] = useState('New Pipeline');
  const [pipelineInstanceId, setPipelineInstanceId] = useState(null);
  const [pipelineDescription, setPipelineDescription] = useState('');
  const [selectedPipeline, setSelectedPipeline] = useState({});
  // const [isSaving, setIsSaving] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [selectedEdge, setSelectedEdge] = useState(null);
  const [executionResults, setExecutionResults] = useState([]);
  const [totalExecutionResults, setTotalExecutionResults] = useState(0);
  const [pipelineLoader, setPipelineLoader] = useState(false);
  // Add these state variables at the top with other state declarations
  const [showRunModal, setShowRunModal] = useState(false);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [showGuardrailsModal, setShowGuardrailsModal] = useState(false);
  const [pipelineInput, setPipelineInput] = useState('{\n  "key": "value"\n}');
  const [resumeInput, setResumeInput] = useState('{\n  "key": "value"\n}');
  const [uploadedDocuments, setUploadedDocuments] = useState([]); // For multiple files
  const [resumeUploadedDocuments, setResumeUploadedDocuments] = useState([]); // For resume modal files
  const [isRunning, setIsRunning] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [pipelineRunFlag, setPipelineRunFlag] = useState(false);
  const [runError, setRunError] = useState(null);
  const [pageNumberNode, setPageNumberNode] = useState(0);
  const [pageSizeNode, setPageSizeNode] = useState(10);
  const [hasMorePages, setHasMorePages] = useState(true);
  const [pollingInterval, setPollingInterval] = useState(null);
  const [pipelineStatus, setPipelineStatus] = useState(null);
  const [docUrls, setDocUrls] = useState([])
  const [inputMode, setInputMode] = useState('json'); // 'json' or 'markdown'
  const [isRunPreviewMode, setIsRunPreviewMode] = useState(false);
  const [isResumePreviewMode, setIsResumePreviewMode] = useState(false);
  const [runVersion, setRunVersion] = useState('v1'); // 'v1' or 'v2'

  const [isEditingName, setIsEditingName] = useState(false);
  const inputRef = useRef(null);
  const savingRef = useRef(false);

  const { user } = useAuth();

  const { permissions, permissionEnum } = usePermission();
  
  
  const userAccessPermissions = permissions?.length
      ? permissions
        .filter(ele => ele.component_type === "pipeline")
        .map(ele => ele.permission)
      : [];

  const { showToast } = useToast();

  // Fetch pipeline data if editing
  useEffect(() => {
    const fetchPipelineById = async () => {
      try {
        let response = await getPipelines({ code: pipelineId });
        let pipeline = response[0];
        setPipelineName(pipeline.name);
        setPipelineDescription(pipeline.description);
        setSelectedPipeline(pipeline);
        setRunVersion(pipeline.running_version || 'v1');
        let convertedPayload = convertPipelineToReactFlow(pipeline);
        setNodes(convertedPayload.nodes);
        setEdges(convertedPayload.edges);
      } catch (error) {
        console.error('Error fetching pipeline:', error);
      }
    };

    if (pipelineId !== 'new') {
      fetchPipelineById();
    } else {
      setNodes([createDefaultTriggerNode()]);
      setEdges([]);
      setPipelineName('New Pipeline');
      setPipelineDescription('');
      setRunVersion('v1');
    }
  }, [pipelineId]);


  useEffect(() => {
    if (isEditingName && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditingName])

  // Keep selectedEdge in sync with edges (fixes textarea cursor jump)
  useEffect(() => {
    if (selectedEdge) {
      const updated = edges.find(e => e.id === selectedEdge.id);
      if (updated) {
        // Only update if the conditions array reference has changed
        if (updated.data?.conditions !== selectedEdge.data?.conditions) {
          setSelectedEdge(updated);
        }
      } else {
        setSelectedEdge(null);
      }
    }
  }, [edges, selectedEdge]);

  const onConnect = useCallback((params) => {
    setEdges((eds) =>
      addEdge(
        {
          ...params,
          animated: true,
          style: { stroke: '#0066ff', strokeWidth: 2 }
        },
        eds
      )
    );
  }, [setEdges]);

  const onEdgeClick = useCallback((event, edge) => {
    setSelectedEdge(edge);
    setSelectedNodeId(null); // Deselect node if edge is selected
    setDrawerOpen(true)
  }, []);

  const onInit = useCallback((instance) => {
    setReactFlowInstance(instance);
  }, []);

  const onNodeClick = useCallback((event, node) => {
    setSelectedNodeId(node.id);
    setSelectedEdge(null);
    // console.log(node, selectedNode)
    setDrawerOpen(true)
  }, []);

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();
      if (!reactFlowInstance || typeof reactFlowInstance.project !== 'function') {
        console.error('ReactFlow instance is not ready or missing .project()');
        return;
      }

      const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
      const type = event.dataTransfer.getData('application/reactflow');
      const nodeData = JSON.parse(event.dataTransfer.getData('application/nodeData'));

      if (!type) return;

      const position = reactFlowInstance.project({
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top
      });

      console.log("type--", type)

      const defaultPropsValues = nodeDefaults[type] || { };

      const newId = `${type}-${Date.now()}`;
      const newNode = {
        id: newId,
        type,
        position,
        data: {
          ...nodeData,
          name: `${nodeData.name} ${nodes.filter(n => n.type === type).length + 1}`,
          code: newId,
          check_list: "",
          post_processors: [],
          pre_procesors: [],
          data: {...defaultPropsValues.data },
          position: {},
          positionAbsolute: {}
        }
      };

      setNodes((nds) => {
        nds = nds.concat(newNode)
        return nds
      });
    },
    [reactFlowInstance, nodes, setNodes]
  );

  // const updateNodeProperties = useCallback((id, data) => {
  //   setNodes((nds) =>
  //     nds.map((node) =>
  //       node.id === id
  //         ? { ...node, data: { ...node.data, ...data } }
  //         : node
  //     )
  //   );
  // }, [setNodes]);

  const updateNodeProperties = useCallback((id, updatedFields) => {
  setNodes((nds) =>
    nds.map((node) => {
      if (node.id === id) {
        // If we are updating the nested 'data' object, deep merge it properly
        if (updatedFields.data) {
          return {
            ...node,
            data: {
              ...node.data,
              ...updatedFields,
              data: {
                ...(node.data?.data || {}),
                ...updatedFields.data,
              },
            },
          };
        }
        
        // Otherwise, handle top-level updates normally
        return {
          ...node,
          data: { ...node.data, ...updatedFields },
        };
      }
      return node;
    })
  );
}, [setNodes]);

  const convertReactFlowToPipeline = useCallback((reactFlowData) => {
    const { nodes, edges, name } = reactFlowData;
    const pipelineNodes = nodes.map((node) => {
      return {
        code: node.code || node.id,
        name: node.name,
        description: node.description,
        stage_name: node.stage_name,
        stage_description: node.stage_description,
        // Backend type (e.g. 'planner', 'entity_operation'), fall back to react type if missing
        type: node.type || node.type,
        agent_code: node.agent_code || null,
        check_list: node.check_list || null,
        pre_procesors: node.pre_procesors || [],
        post_processors: node.post_processors || [],
        data: node.data || {},
        position: node.position || {},
        positionAbsolute: node.positionAbsolute || {},
        ...(node.mcp_servers ? { mcp_servers: node.mcp_servers } : {}),
      };
    });

    const pipelineEdges = edges.map((edge) => ({
      from: edge.source,
      to: edge.target,
      conditions: edge.data?.conditions || ["true"]
    }));

    return {
      code: selectedPipeline.code,
      name: name || "New Pipeline",
      description: selectedPipeline.description || '',
      nodes: pipelineNodes,
      edges: pipelineEdges,
      running_version: selectedPipeline?.running_version || 'v1',
    };
  }, [selectedPipeline]);

  const convertPipelineToReactFlow = (pipelineData) => {
    const { nodes, edges, name } = pipelineData;

    const getReactType = (source) => {
      switch (source) {
        case 'trigger': return 'triggerNode';
        case 'decision_agent': return 'agentNode';
        case 'model': return 'modelNode';
        case 'knowledgebase': return 'knowledgeNode';
        case 'source': return 'dataSourceNode';
        case 'API': return 'apiNode';
        case 'api': return 'apiNode';
        case 'classify': return 'classifierNode';
        case 'webhook': return 'webHookNode';
        case 'emailout': return 'emailOutNode';
        case 'code': return 'codeNode';
        case 'planner': return 'plannerNode';
        case 'entity_operation': return 'entityOperationNode';
        case 'document': return 'documentNode';
        case 'output': return 'outputNode';
        case 'file_input': return 'fileNode';
        case 'manual_task': return 'manualTaskNode';
        case 'task_service': return 'taskServiceNode';
        case 'timer_node' : return 'timerNode';
        default: return source;
      }
    };
    const reactFlowNodes = nodes.map((node) => ({
      id: node.code,
      type: getReactType(node.type),
      position: (node.position === undefined || JSON.stringify(node.position) === '{}') ? { x: Math.random() * 400, y: Math.random() * 400 } : node.position,
      data: { ...node, label: node.name }
    }));

    const reactFlowEdges = edges.map((edge) => ({
      id: `${edge.from}-${edge.to}`,
      source: edge.from,
      target: edge.to,
      animated: true,
      style: { stroke: "#0066ff", strokeWidth: 2 },
      data: { conditions: edge.conditions || ["true"] }
    }));

    return {
      name: name || "New Pipeline",
      nodes: reactFlowNodes,
      edges: reactFlowEdges
    };
  };
  const runPipeline = useCallback(() => {
    setShowRunModal(true);
  }, []);
  const openPropertiesWithoutNodeSelected = useCallback(() => {
    setSelectedNodeId(null);
    setDrawerOpen(true);
  }, []);
  const handleResumePipeline = useCallback(() => {
    setShowResumeModal(true);
  }, []);

  // Utility function to merge new results with existing ones, avoiding duplicates
  const mergeUniqueResults = useCallback((currentResults, newResults, replaceAll = false) => {
    if (!newResults || newResults.length === 0) {
      return replaceAll ? [] : currentResults;
    }

    if (replaceAll) {
      return newResults;
    }

    const existingIds = new Set(currentResults.map(result => result.id));
    const uniqueNewResults = newResults.filter(result => !existingIds.has(result.id));

    return uniqueNewResults.length > 0 ? [...currentResults, ...uniqueNewResults] : currentResults;
  }, []);
 
  // Poll node execution results for the drawer 
  const updateExecutionResults = useCallback(async (pipelineId) => {
    if (!pipelineId) {
      console.warn('Attempted to update execution results without pipeline ID');
      return;
    }
    
    try {
      const filter = { pipeline_id: pipelineId };
      const sort = { created_at: "ASC" };
      const requestId = `node_execution_${pipelineId}_${Date.now()}`;
      
      // Using requestId to help avoid duplicate requests
      const response = await getNodesForPipeline(
        filter, 
        sort, 
        `?skip=0&limit=${pageSizeNode}`,
        requestId
      );

      if (response && Array.isArray(response)) {
        const validatedNodes = response.map((node, index) => {
          if (!node || typeof node !== 'object') {
            console.warn(`Invalid node at index ${index}:`, node);
            return null;
          }

          return {
            id: node.id || `node_${index}`,
            x: node.x || 0,
            y: node.y || 0,
            type: node.type || 'default',
            data: node.data || {},
            position: node.position || { x: 0, y: 0 },
            ...node
          };
        }).filter(Boolean);

        setExecutionResults(validatedNodes);

        setPageNumberNode(0);
        setIsLoadingMore(false);

        if (validatedNodes.length < pageSizeNode) {
          setHasMorePages(false);
        } else {
          setHasMorePages(true);
        }
      }
    } catch (error) {
      // Don't show error for canceled requests
      if (!axios.isCancel(error)) {
        console.error('Error updating execution results:', error);
      }
    }
  }, [pageSizeNode]);

  const checkPipelineStatus = useCallback(async (pipelineId) => {
    try {
      const status = await getPipelineExecutionStatus(pipelineId);
      setPipelineStatus(status);

      if (status && status.transaction_status) {
        const endStates = ['COMPLETED', 'CLOSED', 'ERROR', 'ERRORED', 'WAITING'];
        if (endStates.includes(status.transaction_status.toUpperCase())) {
          // Stop polling if in an end state
          if (pollingInterval) {
            clearInterval(pollingInterval);
            setPollingInterval(null);
          }
          setIsRunning(false);

          if (status.transaction_status.toUpperCase() === 'COMPLETED') {
            showToast({
              message: 'Pipeline completed successfully!',
              type: 'success',
              duration: 5000
            });
          } else if (['ERROR', 'ERRORED'].includes(status.transaction_status.toUpperCase())) {
            showToast({
              message: `Pipeline failed with status: ${status.transaction_status}`,
              type: 'error',
              duration: 8000
            });
          } else if (status.transaction_status.toUpperCase() === 'WAITING') {
            showToast({
              message: 'Pipeline is waiting for input or external trigger',
              type: 'info',
              duration: 5000
            });
          } else {
            showToast({
              message: `Pipeline finished with status: ${status.transaction_status}`,
              type: 'info',
              duration: 5000
            });
          }
          
          // Final execution results update when pipeline is done
          // We'll handle this in the polling function to avoid duplicate requests
          // No need to call updateExecutionResults here
        }
      }

      return status;
    } catch (error) {
      console.error('Error checking pipeline status:', error);
      return null;
    }
  }, [pollingInterval, showToast]);

  // Polling pipeline status
  const startPolling = useCallback((pipelineId) => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
    }

    (async () => {
      try {
        const status = await checkPipelineStatus(pipelineId);
        
        // Only fetch execution results if we need to
        if (status && !['COMPLETED', 'CLOSED', 'ERROR', 'ERRORED'].includes(status.transaction_status?.toUpperCase())) {
          await updateExecutionResults(pipelineId);
        }
      } catch (error) {
        console.error("Initial polling error:", error);
      }
    })();

    // Then set up the interval for future updates
    const interval = setInterval(async () => {
      try {
        const status = await checkPipelineStatus(pipelineId);
        
        // Only fetch execution results if pipeline is still running
        if (status && !['COMPLETED', 'CLOSED', 'ERROR', 'ERRORED'].includes(status.transaction_status?.toUpperCase())) {
          await updateExecutionResults(pipelineId);
        }
      } catch (error) {
        console.error("Polling interval error:", error);
      }
    }, 10000);

    setPollingInterval(interval);
  }, [pollingInterval, checkPipelineStatus, updateExecutionResults]);

  // Stop polling
  const stopPolling = useCallback(() => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
    setIsRunning(false);
  }, [pollingInterval]);

  // Clear polling 
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  //  Clear polling 
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  const executePipeline = async () => {
    setIsRunning(true);
    setRunError(null);

    try {
      let inputData;
      
      // Validate input based on input mode
      if (inputMode === 'json') {
        // JSON validation
        if (!pipelineInput.trim()) {
          throw new Error('Pipeline input cannot be empty when using JSON mode');
        }
        try {
          inputData = JSON.parse(pipelineInput);
          if (inputData === null || inputData === undefined) {
            throw new Error('JSON input cannot be null or undefined');
          }
        } catch (error) {
          throw new Error(`Invalid JSON input: ${error.message}`);
        }
      } else if (inputMode === 'markdown') {
        // Markdown validation
        if (!pipelineInput || pipelineInput.trim() === '') {
          throw new Error('Pipeline input cannot be empty when using Markdown mode');
        }
        // For markdown, we'll pass the raw markdown content as a string
        // wrapped in a structure that the pipeline can understand
        inputData = {
          markdown: pipelineInput.trim()
        };
      } else {
        throw new Error('Invalid input mode selected');
      }
      
      let documentUrls = [];

      if (uploadedDocuments.length > 0) {
        setPipelineRunFlag(true);

        const timestamp = Date.now();

        try {
          if (manualRun) {
            const uploadResults = await Promise.all(
              uploadedDocuments.map((doc, index) => {
                // Add null safety and fallback naming
                const fileName = doc.name || `document_${index}`;
                const cleanFileName = fileName.replace(/[^a-zA-Z0-9.-]/g, '');
                const fileId = `${timestamp}_${cleanFileName}`;

                return uploadFile(
                  doc,
                  "runpipeline",
                  pipelineName,
                  fileId
                );
              })
            );

            const failedUploads = uploadResults.filter(result => result.status !== 'success');
            if (failedUploads.length > 0) {
              const errorMessages = failedUploads.map(result => result.message || 'Unknown error');
              throw new Error(`Document upload failed: ${errorMessages.join(', ')}`);
            }
            documentUrls = uploadResults.map(result => result.data?.url).filter(Boolean);
            if (documentUrls.length !== uploadResults.length) {
              throw new Error('Some uploaded documents did not return valid URLs');
            }
          } else {
            documentUrls = docUrls;
          }

        } catch (error) {
          throw new Error(`Failed to upload documents: ${error.message}`);
        }
      }

      // v1: existing async pipeline executor
      if (runVersion === 'v1') {
        const pipelinePayload = {
          pipeline_id: null,
          document_urls: documentUrls,
          payload: inputData,
          async_execution: true
        };

        const executionResult = await runExecutePipeline(pipelineId, pipelinePayload);

        if (!executionResult || !executionResult.pipeline_id) {
          throw new Error('Pipeline execution failed - no pipeline ID returned');
        }

        showToast({
          message: 'Pipeline started successfully! Checking status...',
          type: 'success',
          duration: 5000,
          onClose: () => {
            console.log('Pipeline execution toast closed');
          }
        });

        startPolling(executionResult.pipeline_id);

      // Reset form state
      setPipelineInput('{\n  "key": "value"\n}');
      setUploadedDocuments([]);

      const filter = { pipeline_id: executionResult.pipeline_id };
      const sort = { created_at: "ASC" };

        // Fetch new nodes from this pipeline execution
        const requestId = `execute_pipeline_${executionResult.pipeline_id}_${Date.now()}`;
        const newNodesResponse = await getNodesForPipeline(
          filter,
          sort,
          `?skip=0&limit=${pageSizeNode}`,
          requestId
        );

      if (!newNodesResponse || !Array.isArray(newNodesResponse)) {
        console.warn('Invalid nodes response:', newNodesResponse);
        throw new Error('Failed to fetch pipeline nodes - invalid response format');
      }

        const validatedNewNodes = newNodesResponse.map((node, index) => {
        if (!node || typeof node !== 'object') {
          console.warn(`Invalid node at index ${index}:`, node);
          return null;
        }

        return {
          id: node.id || `node_${index}`,
          x: node.x || 0,
          y: node.y || 0,
          type: node.type || 'default',
          data: node.data || {},
          position: node.position || { x: 0, y: 0 },
          ...node
        };
        }).filter(Boolean);

        const uniqueResults = mergeUniqueResults(nodes, validatedNewNodes, false);
        console.log({ uniqueResults })

        setPipelineInstanceId(executionResult.pipeline_id);
        setNodes(uniqueResults);

        setExecutionResults(validatedNewNodes);

        setPageNumberNode(0);
        setIsLoadingMore(false);

        if (validatedNewNodes.length < pageSizeNode) {
          setHasMorePages(false);
        } else {
          setHasMorePages(true);
        }
      } else {
        // v2: synchronous workflow executor
        const email = user?.email || user?.username || user?.userName || '';
        if (!email) {
          throw new Error('Logged-in user email is required to run v2 workflow');
        }

        const workflowCode = selectedPipeline?.code || pipelineId;

        const v2Payload = {
          workflow_cd: workflowCode,
          user_id: email,
          query: 'hi',
          payload: inputData || {},
        };
      
        v2Payload['payload']['document_urls'] = documentUrls  || [];

        const v2Result = await runExecutePipelineV2(v2Payload);

        showToast({
          message: 'Pipeline (v2) executed successfully.',
          type: 'success',
          duration: 5000,
        });

        console.log('v2 execution result:', v2Result);
        // For now we do not visualize v2 execution in the analytics drawer.
      }

      if (runVersion === 'v1') {
        setAnalyticsDrawerOpen(true);
      }
      setShowRunModal(false);

    } catch (error) {
      console.error('Pipeline execution failed:', error);

      if (pollingInterval) {
        clearInterval(pollingInterval);
        setPollingInterval(null);
      }

      // Set user-friendly error message
      const errorMessage = error.message || 'An unexpected error occurred during pipeline execution';
      setRunError(errorMessage);

      showToast({
        message: errorMessage,
        type: 'error',
        duration: 8000
      });

    } finally {
      setIsRunning(false);
    }
  };

  const resumePipelineExecution = async () => {
    setIsRunning(true);
    setRunError(null);

    try {
      let inputData;
      
      // Validate input based on input mode
      if (inputMode === 'json') {
        // JSON validation
        if (!resumeInput.trim()) {
          throw new Error('Resume input cannot be empty when using JSON mode');
        }
        try {
          inputData = JSON.parse(resumeInput);
          if (inputData === null || inputData === undefined) {
            throw new Error('JSON input cannot be null or undefined');
          }
        } catch (error) {
          throw new Error(`Invalid JSON input: ${error.message}`);
        }
      } else if (inputMode === 'markdown') {
        // Markdown validation
        if (!resumeInput || resumeInput.trim() === '') {
          throw new Error('Resume input cannot be empty when using Markdown mode');
        }
        // For markdown, we'll pass the raw markdown content as a string
        // wrapped in a structure that the pipeline can understand
        inputData = {
          content: resumeInput.trim(),
          content_type: 'markdown'
        };
      } else {
        throw new Error('Invalid input mode selected');
      }

      let documentUrls = [];

      if (resumeUploadedDocuments.length > 0) {
        const timestamp = Date.now();

        try {
          const uploadResults = await Promise.all(
            resumeUploadedDocuments.map((doc, index) => {
              const fileName = doc.name || `document_${index}`;
              const cleanFileName = fileName.replace(/[^a-zA-Z0-9.-]/g, '');
              const fileId = `${timestamp}_${cleanFileName}`;

              return uploadFile(
                doc,
                "resumepipeline",
                pipelineName,
                fileId
              );
            })
          );

          const failedUploads = uploadResults.filter(result => result.status !== 'success');
          if (failedUploads.length > 0) {
            const errorMessages = failedUploads.map(result => result.message || 'Unknown error');
            throw new Error(`Document upload failed: ${errorMessages.join(', ')}`);
          }

          documentUrls = uploadResults.map(result => result.data?.url).filter(Boolean);

          if (documentUrls.length !== uploadResults.length) {
            throw new Error('Some uploaded documents did not return valid URLs');
          }

        } catch (error) {
          throw new Error(`Failed to upload documents: ${error.message}`);
        }
      }

      const resumePayload = {
        pipeline_id: pipelineInstanceId,
        document_urls: documentUrls,
        payload: inputData,
        async_execution: true
      };

      const resumeResult = await resumePipeline(pipelineInstanceId, resumePayload);

      if (!resumeResult) {
        throw new Error('Pipeline resume failed - no response returned');
      }

      showToast({
        message: 'Pipeline resumed successfully! Checking status...',
        type: 'success',
        duration: 5000
      });

      // Start polling for the resumed pipeline
      startPolling(pipelineInstanceId);

      // Reset form state
      setResumeInput('{\n  "key": "value"\n}');
      setResumeUploadedDocuments([]);

      // Update execution results
      await updateExecutionResults(pipelineInstanceId);

      setShowResumeModal(false);

    } catch (error) {
      console.error('Pipeline resume failed:', error);

      if (pollingInterval) {
        clearInterval(pollingInterval);
        setPollingInterval(null);
      }

      const errorMessage = error.message || 'An unexpected error occurred during pipeline resume';
      setRunError(errorMessage);

      showToast({
        message: errorMessage,
        type: 'error',
        duration: 8000
      });

    } finally {
      setIsRunning(false);
    }
  };

  const refreshResults = useCallback(async () => {
    setPipelineLoader(true);
    if (!pipelineInstanceId) return;

    try {
      // Check status and pdate results
      if (pipelineInstanceId) {
        await checkPipelineStatus(pipelineInstanceId);
      }

      await updateExecutionResults(pipelineInstanceId);

      setPipelineLoader(false);
    }
    catch (error) {
      setPipelineLoader(false);
      console.error('Error refreshing results:', error);
      setRunError(error.message || 'Failed to refresh results');
    }
  }, [pipelineInstanceId, checkPipelineStatus, updateExecutionResults]);

  // Handle pagination for infinite scroll
  const handlePaginationChange = useCallback(async (event) => {
    if (event === 'next' && !isLoadingMore && hasMorePages) {
      setIsLoadingMore(true);
      const nextPage = pageNumberNode + 1;

      try {
        const filter = { pipeline_id: pipelineInstanceId };
        const sort = { created_at: "ASC" };

        // Fetch next page with skip and limit
        const requestId = `pagination_${pipelineInstanceId}_${nextPage}_${Date.now()}`;
        const response = await getNodesForPipeline(
          filter,
          sort,
          `?skip=${nextPage * pageSizeNode}&limit=${pageSizeNode}`,
          requestId
        );

        if (response && response.length > 0) {
          // Use utility function to merge unique results
          setExecutionResults(prevResults => mergeUniqueResults(prevResults, response));
          setPageNumberNode(nextPage);

          if (response.length < pageSizeNode) {
            console.log('Reached end of data - no more pages available');
            setHasMorePages(false);
          }
        } else {
          // No data returned, reached the end
          setHasMorePages(false);
        }
      } catch (error) {
        // Don't log canceled requests as errors
        if (!axios.isCancel(error)) {
          console.error('Error loading more results:', error);
        }
      } finally {
        setIsLoadingMore(false);
      }
    }
  }, [pipelineInstanceId, pageNumberNode, pageSizeNode, isLoadingMore, hasMorePages, mergeUniqueResults]);

  // Define CrudFunction first to avoid dependency issues
  // const CrudFunction = useCallback(async (payload) => {
  //   if (selectedPipeline.id) {
  //     Object.assign(selectedPipeline, {
  //       ...payload,
  //       name: payload.name,
  //       description: payload.description || selectedPipeline.description
  //     });
  //     await updatePipeline(selectedPipeline);
  //   } else {
  //     Object.assign(selectedPipeline, {
  //       ...payload,
  //       description: payload.description || payload.name, // Use the provided description if available
  //       code: "Pipeline-" + new Date().getTime()
  //     });
  //     await createPipeline(selectedPipeline);
  //   }
  // }, [selectedPipeline]);

  const CrudFunction = useCallback(async (payload) => {
  if (selectedPipeline?.id) {
    const updatedPipeline = {
      ...selectedPipeline,
      ...payload,
      description: payload.description ?? selectedPipeline.description,
    };

    await updatePipeline(updatedPipeline);
    return updatedPipeline;
  } else {
    const newPipeline = {
      ...selectedPipeline,
      ...payload,
      code: "Pipeline-" + Date.now(),
      description: payload.description ?? payload.name,
    };

    const created = await createPipeline(newPipeline);
    return created;
  }
}, [selectedPipeline, updatePipeline, createPipeline]);

  const validateMandetoryNodeProperties = () => {
    const mandetoryFieldConfig = mandatoryFields;

    validationLoop : for(const node of nodes) {
      const mandotoryFieldsForType = mandetoryFieldConfig[node?.type];
      if(mandotoryFieldsForType) {
        for(const field of mandotoryFieldsForType) {
          const value = node?.data?.data[field];
          if(!value || value == "") {
            const nodeName = convertNodeNameToLocal(node?.type);
            const fieldName = field
              .split('_')
              .map(e => e.charAt(0).toUpperCase() + e.slice(1))
              .join(' ');

            showToast({ 
              message: `The ${fieldName} field is mandatory inside the ${nodeName} to save the pipeline.`,
              type: "error",
              duration: 5000
            });

            return false;
          }
        }
      }
    }
    return true;
  }
  
  const savePipeline = useCallback(async () => {
    console.log('Saving pipeline...', selectedPipeline);
    if (savingRef.current) return;
    const isValidNodeProps = validateMandetoryNodeProperties();
    if(!isValidNodeProps) {
        console.log('Validation failed. Aborting save process.');
        return;
    }
    console.log('Pipeline description when saving:', selectedPipeline.description);
    savingRef.current = true;
    setError(null);
    
    try {
      if (!pipelineName.trim()) throw new Error('Pipeline name is required');

      const updatedEdges = edges.map(edge => ({
        ...edge,
        from: edge.source,
        to: edge.target,
        conditions: ["true"]
      }));

      const nodesForPayload = nodes.map(node => ({
        ...node.data,
        position: node.position,
        positionAbsolute: node.positionAbsolute ?? node.position
      }));

      const pipelineData = {
        name: pipelineName,
        nodes: nodesForPayload,
        edges: updatedEdges,
        description: selectedPipeline.description || '',
      };
      removeEmptyHeaders(pipelineData);
      // Normalize input_schema_config to ensure required 'source' key is present
      const existingSchema = selectedPipeline?.input_schema_config || {};
      const normalizedSchema = { ...existingSchema };
      if (normalizedSchema && !normalizedSchema.source) {
        if (normalizedSchema.sample_example) {
          normalizedSchema.source = 'example';
        } else if (normalizedSchema.value && typeof normalizedSchema.value === 'object') {
          normalizedSchema.source = 'dict';
        } else if (normalizedSchema.entity_cd) {
          normalizedSchema.source = 'entity';
        }
      }

      const basePayload = convertReactFlowToPipeline(pipelineData);
      const resultedPipeline = await CrudFunction({ ...basePayload, input_schema_config: normalizedSchema });
      if (!resultedPipeline) {
        throw new Error('Invalid response from server while saving pipeline');
      }
      console.log("result after savined pipeline", resultedPipeline)
      setSelectedPipeline(resultedPipeline)
      setIsSuccess(true);
      setTimeout(() => setIsSuccess(false), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      savingRef.current = false;
    }
  }, [pipelineName, nodes, edges, selectedPipeline, CrudFunction, convertReactFlowToPipeline]);

  // const clearCanvas = useCallback(() => {
  //   if (window.confirm('Are you sure you want to clear the canvas? All unsaved changes will be lost.')) {
  //     setNodes([]);
  //     setEdges([]);
  //     setSelectedNode(null);
  //   }
  // }, []);

  const updateEdgeProperties = useCallback((id, data) => {
    setEdges((edges) =>
      edges.map((edge) =>
        edge.id === id
          ? { ...edge, data: { ...edge.data, ...data } }
          : edge
      )
    );
  }, [setEdges]);

  const handleNavigateAway = () => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
    navigate('/agent-pipelines');
  };

 const removeEmptyHeaders = (pipelineData) => {
  try {
    pipelineData.nodes.forEach((node) => {
      if (node.type === "API" && node.data.headers) {
        for (let key in node.data.headers) {
          if (node.data.headers.hasOwnProperty(key) && (key === "" || node.data.headers[key] === "")) {
            delete node.data.headers[key]; 
          }
        }

        if (Object.keys(node.data.headers).length === 0) {
          delete node.data.headers; 
        }
      }
    });

  } catch (error) {
    console.error("Error while removing empty headers", error);
  }
}



  const [drawerOpen, setDrawerOpen] = useState(false);
  const [analyticsDrawerOpen, setAnalyticsDrawerOpen] = useState(false);
  const [drawerWidth, setDrawerWidth] = useState(540);
  const [lastPipelineRunDetails, setLastPipeLIneRunDetails] = useState({});
  const [manualRun, setManulRun] = useState(true);
  const drawerResizing = useRef(false);

  const onDrawerMouseDown = () => {
    drawerResizing.current = true;
    document.body.style.cursor = 'ew-resize';
  };

  useEffect(() => {
    const onMouseMove = (e) => {
      if (drawerResizing.current) {
        setDrawerWidth((prev) => Math.max(280, Math.min(700, prev - e.movementX)));
      }
    };
    const onMouseUp = () => {
      drawerResizing.current = false;
      document.body.style.cursor = '';
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, []);


  useEffect(() => {
    const getLastPipeline = async () => {
      const data = await getPipelineRuns({ code: pipelineId }, {created_at: "DESC"}, "skip=0&limit=1");
      console.log("Last pipeline run data : ", data)
      if (data) {
        let payload = data[0]?.request;
        if(payload){
          setLastPipeLIneRunDetails({ 'payload': payload });
        }
      }
    }

    getLastPipeline();
  }, [])


  const convertFilePathsWithFolder = (filePathArray) => {
    return filePathArray?.map(filePath => {
      const pathAfterRunpipeline = filePath.split('/runpipeline/')[1];

      return {
        name: pathAfterRunpipeline,
        size: 30000
      };
    });
  };


  const handleManualRun = (manualFl) => {
    setManulRun(manualFl);
    if (!manualFl) {
      const docUrl = lastPipelineRunDetails?.payload?.document_urls;
      setDocUrls(docUrl);
      const files = convertFilePathsWithFolder(docUrl)
      setUploadedDocuments(files)
      setPipelineInput(JSON.stringify(lastPipelineRunDetails?.payload, null, 2))
    }
  }
  const temp = (testPayload) => {
    console.log("Pipeline properties updated:", testPayload);
    setSelectedPipeline(prevState => {
      // Create a new state object with all properties from both prevState and testPayload
      const newState = { 
        ...prevState,
        ...testPayload, 
        value_stream: testPayload.value_stream !== undefined ? testPayload.value_stream : prevState.value_stream,
        entity_name: testPayload.entity_name !== undefined ? testPayload.entity_name : prevState.entity_name,
        knowledgebase: testPayload.knowledgebase !== undefined ? testPayload.knowledgebase : prevState.knowledgebase,
      };

      if (testPayload.associated_entities !== undefined) {
        newState.associated_entities = testPayload.associated_entities.map(entity => {
          if (typeof entity === 'string') {
            return { entity_name: entity };
          }
          if (entity.name && !entity.entity_name) {
            return { entity_name: entity.name };
          }
          return entity;
        });
      } else {
        if (prevState.associated_entities && prevState.associated_entities.length > 0) {
          newState.associated_entities = prevState.associated_entities.map(entity => {
            if (typeof entity === 'string') {
              return { entity_name: entity };
            }
            if (entity.name && !entity.entity_name) {
              return { entity_name: entity.name };
            }
            return entity;
          });
        } else {
          newState.associated_entities = prevState.associated_entities || [];
        }
      }
      
      return newState;
    });
  }

  return (
   <PageContainer style={{ padding: '0.5rem 1rem ' }}>
      <motion.div initial="hidden" animate="visible" variants={fadeIn} style={{ height: '100%' }}>
        <DesignerHeader>
          {/* Left section */}
          <HeaderLeft>
            <BackButton onClick={handleNavigateAway}>
              <FontAwesomeIcon style={{ color: '#1d4ed8' }} icon={faChevronCircleLeft} />
            </BackButton>

            <PipelineNameWrapper onClick={() => setIsEditingName(true)}>
              <PipelineNameInput
                ref={inputRef}
                value={pipelineName}
                onChange={(e) => setPipelineName(e.target.value)}
                placeholder="Untitled workflow"
                $editing={isEditingName}
                onFocus={() => setIsEditingName(true)}
                onBlur={() => setIsEditingName(false)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setIsEditingName(false);
                  }
                  if (e.key === 'Escape') {
                    setIsEditingName(false);
                    inputRef.current.blur();
                  }
                }}
              />
            </PipelineNameWrapper>

          </HeaderLeft>

          {/* Right section */}
          <HeaderRight>
            <HeaderButton
              onClick={openPropertiesWithoutNodeSelected}
              title="Pipeline settings"
            >
              <FontAwesomeIcon style={{ fontSize: '16px' }} icon={faCog} />
            </HeaderButton>
            <HeaderButton
              onClick={() => setShowGuardrailsModal(true)}
              title="Content Guardrails"
            >
              <FontAwesomeIcon style={{ fontSize: '16px' }} icon={faShield} />
              <span>Guardrails</span>
            </HeaderButton>

            <HeaderButton
              onClick={runPipeline}
              disabled={isRunning}
              title="Run"
            >
              <FontAwesomeIcon icon={faPlay} />
              <span>Run</span>
            </HeaderButton>

            <HeaderButton
              primary
              onClick={savePipeline}
              disabled={savingRef.current}
              title="Save"
            >
              <FontAwesomeIcon icon={faSave} />
              <span>{savingRef.current ? 'Saving...' : 'Save'}</span>
            </HeaderButton>
          </HeaderRight>
        </DesignerHeader>

        {error && <div style={{ padding: '0.5rem', background: '#ffe0e0', color: '#b00020', borderRadius: '4px' }}>Error: {error}</div>}
        {isSuccess && <div style={{ padding: '0.5rem', background: '#d4edda', color: '#155724', borderRadius: '4px' }}>Pipeline saved successfully!</div>}

        <DesignerContainer>
          <NodePaletteContainer>
            <NodePalette />
          </NodePaletteContainer>
          <FlowContainer ref={reactFlowWrapper} >
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              onInit={onInit}
              onNodeClick={onNodeClick}
              onEdgeClick={onEdgeClick}
              onDrop={onDrop}
              onDragOver={onDragOver}
              nodeTypes={nodeTypes}
              fitView={false}
              snapToGrid
              snapGrid={[15, 15]}
              defaultZoom={1.5}
              minZoom={0.2}
              maxZoom={4}
              deleteKeyCode="Delete"
            >
              <Controls />
              <MiniMap />

              <Background color="#f0f0f0" gap={16} />
            </ReactFlow>
          </FlowContainer>
          {/* <PropertiesPanel
            selectedNode={selectedNode}
            updateNodeProperties={updateNodeProperties}
            pipelineDescription={pipelineDescription}
            setPipelineDescription={setPipelineDescription}
            selectedEdge={selectedEdge}
            updateEdgeProperties={updateEdgeProperties}
          /> */}
        </DesignerContainer>
        {/* Properties Panel */}
        {pipelineRunFlag ? (
          <DrawerToggleButton
            onClick={() => setDrawerOpen((open) => !open)}
            drawerOpen={drawerOpen}
            drawerWidth={drawerWidth}
          >
            {drawerOpen ? (
              <FaTimes size={22} />
            ) : (
              <FaSlidersH size={22} />
            )}
          </DrawerToggleButton>
        ) : (<>
          <DrawerToggleButton
            onClick={() => setDrawerOpen((open) => !open)}
            drawerOpen={drawerOpen}
            drawerWidth={drawerWidth}
          >
            {drawerOpen ? (
              <FaTimes size={22} />
            ) : (
              <FaSlidersH size={22} />
            )}
          </DrawerToggleButton>
        </>)}

        <FloatingDrawer open={drawerOpen} width={drawerWidth} style={{ zIndex: 9991 }}>
          <DrawerResizer onMouseDown={onDrawerMouseDown} />
          <PropertiesPanel
            selectedNode={selectedNode}
            updateNodeProperties={updateNodeProperties}
            pipelineDescription={pipelineDescription}
            setPipelineDescription={setPipelineDescription}
            selectedPipeline={selectedPipeline}
            setSelectedPipeline={temp}
            selectedEdge={selectedEdge}
            updateEdgeProperties={updateEdgeProperties}
          />
        </FloatingDrawer>

        {/* Analytics Panel */}

        {pipelineRunFlag ? (
          <DrawerToggleButton
            onClick={() => setAnalyticsDrawerOpen((open) => !open)}
            drawerOpen={analyticsDrawerOpen}
            drawerWidth={drawerWidth}
          >
            {analyticsDrawerOpen ? (
              <FaTimes size={22} />
            ) : (
              <FaSlidersH size={22} />
            )}
          </DrawerToggleButton>
        ) : (<></>)}
        <FloatingDrawer open={analyticsDrawerOpen} width={drawerWidth} style={{ zIndex: 9991 }}>
          <DrawerResizer onMouseDown={onDrawerMouseDown} />
          <PipelineExecutionDrawer
            isOpen={analyticsDrawerOpen}
            onClose={() => setAnalyticsDrawerOpen(false)}
            onRefresh={() => {
              refreshResults()
            }}
            onResume={handleResumePipeline}
            onPaginationChange={handlePaginationChange}
            executionResults={executionResults}
            isRunning={isRunning}
            isLoadingMore={isLoadingMore}
            pipelineName={pipelineName}
            totalExecutionResults={totalExecutionResults}
            pipelineStatus={pipelineStatus}
            pollingInterval={pollingInterval}
            showCloseButton={true}
          />
        </FloatingDrawer>
      </motion.div>
      {showRunModal && (
        <Modal
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setShowRunModal(false)}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '8px',
              padding: '2rem',
              width: '80%'
            }}
          >
            <SubSectionTitle>Run Pipeline: {pipelineName}</SubSectionTitle>
             <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                margin: '0.5rem 0 0.75rem',
              }}
            >
              <div style={{ fontSize: '0.9rem', color: '#555' }}>Pipeline version</div>
              <select
                value={runVersion}
                onChange={(e) => setRunVersion(e.target.value)}
                style={{
                  padding: '0.25rem 0.5rem',
                  borderRadius: 4,
                  border: '1px solid #ccc',
                  fontSize: '0.85rem',
                  minWidth: 180,
                }}
              >
                <option value="v1">v1 (async pipeline)</option>
                <option value="v2">v2 (workflow executor)</option>
              </select>
            </div>
            {pipelineStatus && (
              <div style={{
                marginBottom: '1rem',
                padding: '0.75rem',
                background: pipelineStatus.transaction_status === 'COMPLETED' ? '#d4edda' :
                  ['ERROR', 'ERRORED'].includes(pipelineStatus.transaction_status?.toUpperCase()) ? '#f8d7da' :
                    '#f0f8ff',
                borderRadius: '4px',
                fontSize: '0.9rem',
                border: `1px solid ${pipelineStatus.transaction_status === 'COMPLETED' ? '#c3e6cb' :
                  ['ERROR', 'ERRORED'].includes(pipelineStatus.transaction_status?.toUpperCase()) ? '#f5c6cb' :
                    '#bee5eb'}`
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <strong>Status:</strong> {pipelineStatus.transaction_status || 'Unknown'}
                  </div>
                  {pollingInterval && (
                    <div style={{
                      fontSize: '0.8rem',
                      color: '#666',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem'
                    }}>
                      <div style={{
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        background: '#4caf50'
                      }}></div>
                      Checking every 10s...
                    </div>
                  )}
                </div>
                {pipelineStatus.created_at && (
                  <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: '#666' }}>
                    Last updated: {new Date(pipelineStatus.created_at).toLocaleString()}
                  </div>
                )}
              </div>
            )}
            <Divider />
            {runError && (
              <div style={{
                padding: '0.75rem',
                background: '#ffe0e0',
                color: '#b00020',
                borderRadius: '4px',
                marginBottom: '1rem'
              }}>
                Error: {runError}
              </div>
            )}
            <div style={{ marginBottom: '1.5rem', display: 'flex', gap: '1rem', alignItems: 'top' }}>
              <div style={{ flex: '1 1 60%' }}>
                <div style={{ flex: '1 1 60%' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <label style={{ fontWeight: 500 }}>
                      Pipeline Input ({inputMode === 'json' ? 'JSON' : 'Markdown'})
                    </label>
                    <div style={{ display: 'flex', gap: '1rem' }}>
                      <label style={{ display: 'flex', alignItems: 'center', fontSize: '0.9rem', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="pipelineOption"
                          value="true"
                          checked={manualRun === true}
                          onChange={() => handleManualRun(true)}
                          style={{ marginRight: '0.25rem' }}
                        />
                        Manual Run
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', fontSize: '0.9rem', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="pipelineOption"
                          value="false"
                          checked={manualRun === false}
                          onChange={() => handleManualRun(false)}
                          style={{ marginRight: '0.25rem' }}
                        />
                        Use Previous Run Input
                      </label>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <button
                      type="button"
                      onClick={() => {setInputMode('json'); setPipelineInput('{\n  "key": "value"\n}');}}
                      style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '4px',
                        border: '1px solid #ccc',
                        background: inputMode === 'json' ? '#007bff' : '#f8f9fa',
                        color: inputMode === 'json' ? 'white' : '#333',
                        cursor: 'pointer',
                        fontSize: '0.8rem'
                      }}
                    >
                      JSON
                    </button>
                    <button
                      type="button"
                      onClick={() => {setInputMode('markdown'); setPipelineInput('');}}
                      style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '4px',
                        border: '1px solid #ccc',
                        background: inputMode === 'markdown' ? '#007bff' : '#f8f9fa',
                        color: inputMode === 'markdown' ? 'white' : '#333',
                        cursor: 'pointer',
                        fontSize: '0.8rem'
                      }}
                    >
                      Markdown
                    </button>
                  </div>
                  {inputMode === 'json' ? (
                    <Textarea
                      value={pipelineInput}
                      onChange={(e) => setPipelineInput(e.target.value)}
                      style={{
                        width: '100%',
                        height: '200px',
                        padding: '0.75rem',
                        borderRadius: '4px',
                        border: '1px solid #ccc',
                        fontFamily: 'monospace',
                        fontSize: '0.9rem'
                      }}
                      placeholder='Enter JSON input for the pipeline'
                    />
                  ) : (
                    <MarkdownEditorContainer>
                      <ToggleButton
                        active={isRunPreviewMode}
                        onClick={() => setIsRunPreviewMode(!isRunPreviewMode)}
                        type="button"
                      >
                        <FontAwesomeIcon icon={isRunPreviewMode ? faEdit : faEye} />
                        {isRunPreviewMode ? 'Edit' : 'Preview'}
                      </ToggleButton>
                      
                      {isRunPreviewMode ? (
                        <MarkdownPreview>
                          {pipelineInput ? (
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                              {pipelineInput}
                            </ReactMarkdown>
                          ) : (
                            <p style={{ color: '#6c757d', fontStyle: 'italic' }}>
                              No content provided
                            </p>
                          )}
                        </MarkdownPreview>
                      ) : (
                        <Textarea
                          value={pipelineInput}
                          onChange={(e) => setPipelineInput(e.target.value || '')}
                          placeholder="Enter markdown content..."
                          style={{
                            width: '100%',
                            height: '200px',
                            padding: '0.75rem',
                            border: 'none',
                            borderRadius: '0',
                            fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                            fontSize: '0.9rem',
                            resize: 'none',
                            outline: 'none'
                          }}
                        />
                      )}
                    </MarkdownEditorContainer>
                  )}
                </div>
              </div>

              <div style={{ flex: '1 1 auto' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                  Upload Document (Optional)
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <input
                    id="file-upload"
                    type="file"
                    multiple
                    style={{ display: 'none' }}
                    onChange={e => {
                        if (e.target.files && e.target.files.length > 0) {
                          setUploadedDocuments(prev => {
                            const safePrev = prev || [];
                            const existing = new Set(safePrev?.map(f => f.name + f.size));
                            const newFiles = Array.from(e.target.files).filter(
                              f => !existing.has(f.name + f.size)
                            );
                            return [...safePrev, ...newFiles];
                          });
                        }
                      /* Reset input */
                      e.target.value = '';
                    }}
                  />
                  <Button
                    type="button"
                    onClick={() => document.getElementById('file-upload').click()}
                    style={{ minWidth: 120 }}
                  >
                    Upload File(s)
                  </Button>
                </div>
                {uploadedDocuments && uploadedDocuments.length > 0 && (
                  <div
                    style={{
                      padding: '0.5rem 0.75rem',
                      borderRadius: '4px',
                      fontSize: '0.9rem',
                      marginTop: '0.75rem'
                    }}
                  >
                    {uploadedDocuments.map((file, idx) => (
                      <div
                        key={file.name + file.size + idx}
                        style={{
                          background: '#e6f7ff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          marginBottom: 4,
                          marginTop: '.5rem',
                          padding: '0.25rem 0.5rem',
                          borderRadius: 4,
                        }}
                      >
                        <span>
                          {file.name} <br /> <small>({Math.round(file.size / 1024)} KB)</small>
                        </span>
                        <Button
                          type="button"
                          style={{
                            background: '#ffeded',
                            color: '#b00020',
                            border: 'none',
                            borderRadius: 4,
                            padding: '2px 8px',
                            marginLeft: 8,
                            fontSize: 12,
                            cursor: 'pointer'
                          }}
                          onClick={() =>
                            setUploadedDocuments(prev =>
                              prev.filter((_, i) => i !== idx)
                            )
                          }
                        >
                          <FontAwesomeIcon icon={faTrash} size="sm" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
              <PipelineButtons 
                onClick={() => setShowRunModal(false)} 
                title="Cancel"
                style={{ minWidth: '40px', padding: '0.5rem' }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </PipelineButtons>
              {pollingInterval && (
                <PipelineButtons
                  onClick={stopPolling}
                  style={{ background: '#ff6b6b', color: 'white', minWidth: '40px', padding: '0.5rem' }}
                  title="Stop Polling"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </PipelineButtons>
              )}
              <PipelineButtons
                primary
                onClick={executePipeline}
                disabled={isRunning}
                title={isRunning ? 'Running...' : 'Run Pipeline'}
                style={{ minWidth: '40px', padding: '0.5rem' }}
              >
                <FontAwesomeIcon icon={faPlay} />
                {isRunning && <span style={{ marginLeft: '8px', fontSize: '0.8rem' }}>...</span>}
              </PipelineButtons>
            </div>
          </div>
        </Modal>
      )}
      {showGuardrailsModal && (
        <Modal
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setShowGuardrailsModal(false)}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: '10px',
              padding: '1rem',
              width: 'min(980px, 94vw)',
              maxHeight: '86vh',
              overflow: 'auto'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <SubSectionTitle style={{ margin: 0 }}>Content Guardrails</SubSectionTitle>
              <Button onClick={() => setShowGuardrailsModal(false)} title="Close">Close</Button>
            </div>
            <GuardrailsEditor
              aiGuardrails={selectedPipeline?.content_guardrails || []}
              onChange={(list) => setSelectedPipeline(prev => ({ ...prev, content_guardrails: list }))}
            />
          </div>
        </Modal>
      )}
      {showResumeModal && (
        <Modal
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setShowResumeModal(false)}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '8px',
              padding: '2rem',
              width: '80%'
            }}
          >
            <SubSectionTitle>Resume Pipeline: {pipelineName}</SubSectionTitle>
            {pipelineStatus && (
              <div style={{
                marginBottom: '1rem',
                padding: '0.75rem',
                background: pipelineStatus.transaction_status === 'COMPLETED' ? '#d4edda' :
                  ['ERROR', 'ERRORED'].includes(pipelineStatus.transaction_status?.toUpperCase()) ? '#f8d7da' :
                    '#f0f8ff',
                borderRadius: '4px',
                fontSize: '0.9rem',
                border: `1px solid ${pipelineStatus.transaction_status === 'COMPLETED' ? '#c3e6cb' :
                  ['ERROR', 'ERRORED'].includes(pipelineStatus.transaction_status?.toUpperCase()) ? '#f5c6cb' :
                    '#bee5eb'}`
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <strong>Status:</strong> {pipelineStatus.transaction_status || 'Unknown'}
                  </div>
                  {pollingInterval && (
                    <div style={{
                      fontSize: '0.8rem',
                      color: '#666',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem'
                    }}>
                      <div style={{
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        background: '#4caf50'
                      }}></div>
                      Checking every 10s...
                    </div>
                  )}
                </div>
                {pipelineStatus.created_at && (
                  <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: '#666' }}>
                    Last updated: {new Date(pipelineStatus.created_at).toLocaleString()}
                  </div>
                )}
              </div>
            )}
            <Divider />
            {runError && (
              <div style={{
                padding: '0.75rem',
                background: '#ffe0e0',
                color: '#b00020',
                borderRadius: '4px',
                marginBottom: '1rem'
              }}>
                Error: {runError}
              </div>
            )}
            <div style={{ marginBottom: '1.5rem', display: 'flex', gap: '1rem', alignItems: 'top' }}>
              <div style={{ flex: '1 1 60%' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                  Pipeline Input ({inputMode === 'json' ? 'JSON' : 'Markdown'})
                </label>
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <button
                    type="button"
                    onClick={() => setInputMode('json')}
                    style={{
                      padding: '0.25rem 0.75rem',
                      borderRadius: '4px',
                      border: '1px solid #ccc',
                      background: inputMode === 'json' ? '#007bff' : '#f8f9fa',
                      color: inputMode === 'json' ? 'white' : '#333',
                      cursor: 'pointer',
                      fontSize: '0.8rem'
                    }}
                  >
                    JSON
                  </button>
                  <button
                    type="button"
                    onClick={() => setInputMode('markdown')}
                    style={{
                      padding: '0.25rem 0.75rem',
                      borderRadius: '4px',
                      border: '1px solid #ccc',
                      background: inputMode === 'markdown' ? '#007bff' : '#f8f9fa',
                      color: inputMode === 'markdown' ? 'white' : '#333',
                      cursor: 'pointer',
                      fontSize: '0.8rem'
                    }}
                  >
                    Markdown
                  </button>
                </div>
                {inputMode === 'json' ? (
                  <Textarea
                    value={resumeInput}
                    onChange={(e) => setResumeInput(e.target.value)}
                    style={{
                      width: '100%',
                      height: '200px',
                      padding: '0.75rem',
                      borderRadius: '4px',
                      border: '1px solid #ccc',
                      fontFamily: 'monospace',
                      fontSize: '0.9rem'
                    }}
                    placeholder='Enter JSON input for the pipeline'
                  />
                ) : (
                  <MarkdownEditorContainer>
                    <ToggleButton
                      active={isResumePreviewMode}
                      onClick={() => setIsResumePreviewMode(!isResumePreviewMode)}
                      type="button"
                    >
                      <FontAwesomeIcon icon={isResumePreviewMode ? faEdit : faEye} />
                      {isResumePreviewMode ? 'Edit' : 'Preview'}
                    </ToggleButton>
                    
                    {isResumePreviewMode ? (
                      <MarkdownPreview>
                        {resumeInput ? (
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {resumeInput}
                          </ReactMarkdown>
                        ) : (
                          <p style={{ color: '#6c757d', fontStyle: 'italic' }}>
                            No content provided
                          </p>
                        )}
                      </MarkdownPreview>
                    ) : (
                      <Textarea
                        value={resumeInput}
                        onChange={(e) => setResumeInput(e.target.value || '')}
                        placeholder="Enter markdown content..."
                        style={{
                          width: '100%',
                          height: '200px',
                          padding: '0.75rem',
                          border: 'none',
                          borderRadius: '0',
                          fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                          fontSize: '0.9rem',
                          resize: 'none',
                          outline: 'none'
                        }}
                      />
                    )}
                  </MarkdownEditorContainer>
                )}
              </div>

              <div style={{ flex: '1 1 auto' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                  Upload Document (Optional)
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <input
                    id="file-upload-resume"
                    type="file"
                    multiple
                    style={{ display: 'none' }}
                    onChange={e => {
                      if (e.target.files && e.target.files.length > 0) {
                        setResumeUploadedDocuments(prev => {
                          const existing = new Set(prev.map(f => f.name + f.size));
                          const newFiles = Array.from(e.target.files).filter(
                            f => !existing.has(f.name + f.size)
                          );
                          return [...prev, ...newFiles];
                        });
                      }
                      /* Reset input */
                      e.target.value = '';
                    }}
                  />
                  <Button
                    type="button"
                    onClick={() => document.getElementById('file-upload-resume').click()}
                    style={{ minWidth: 120 }}
                  >
                    Upload File(s)
                  </Button>
                </div>
                {resumeUploadedDocuments && resumeUploadedDocuments.length > 0 && (
                  <div
                    style={{
                      padding: '0.5rem 0.75rem',
                      borderRadius: '4px',
                      fontSize: '0.9rem',
                      marginTop: '0.75rem'
                    }}
                  >
                    {resumeUploadedDocuments.map((file, idx) => (
                      <div
                        key={file.name + file.size + idx}
                        style={{
                          background: '#e6f7ff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          marginBottom: 4,
                          marginTop: '.5rem',
                          padding: '0.25rem 0.5rem',
                          borderRadius: 4,
                        }}
                      >
                        <span>
                          {file.name} <br /> <small>({Math.round(file.size / 1024)} KB)</small>
                        </span>
                        <Button
                          type="button"
                          style={{
                            background: '#ffeded',
                            color: '#b00020',
                            border: 'none',
                            borderRadius: 4,
                            padding: '2px 8px',
                            marginLeft: 8,
                            fontSize: 12,
                            cursor: 'pointer'
                          }}
                          onClick={() =>
                            setResumeUploadedDocuments(prev =>
                              prev.filter((_, i) => i !== idx)
                            )
                          }
                        >
                          <FontAwesomeIcon icon={faTrash} size="sm" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
              <Button 
                onClick={() => setShowResumeModal(false)} 
                title="Cancel"
                style={{ minWidth: '40px', padding: '0.5rem' }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </Button>
              {pollingInterval && (
                <Button
                  onClick={stopPolling}
                  style={{ background: '#ff6b6b', color: 'white', minWidth: '40px', padding: '0.5rem' }}
                  title="Stop Polling"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </Button>
              )}
              <Button
                primary
                onClick={resumePipelineExecution}
                disabled={isRunning}
                title={isRunning ? 'Resuming...' : 'Resume Pipeline'}
                style={{ minWidth: '40px', padding: '0.5rem' }}
              >
                <FontAwesomeIcon icon={faPlay} />
                {isRunning && <span style={{ marginLeft: '8px', fontSize: '0.8rem' }}>...</span>}
              </Button>
            </div>
          </div>
        </Modal>
      )}

    </PageContainer>
  );
};

export default PipelineDesigner;